import { useNavigate } from "react-router-dom";

const sharedStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0e1a;
    --surface: #111827;
    --surface2: #1a2235;
    --border: rgba(255,255,255,0.07);
    --accent: #00d4ff;
    --accent2: #7b5ea7;
    --text: #e8edf5;
    --muted: #6b7a99;
    --font-display: 'Syne', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    min-height: 100vh;
  }

  .page {
    min-height: 100vh;
    background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%),
                var(--bg);
  }

  .nav {
    padding: 20px 48px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,14,26,0.85);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--text);
    letter-spacing: 0.02em;
    cursor: pointer;
  }

  .nav-logo span { font-size: 26px; }
  .nav-actions { display: flex; align-items: center; gap: 12px; }

  .btn-ghost {
    background: transparent;
    color: var(--muted);
    border: 1px solid var(--border);
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }

  .btn-accent {
    background: var(--accent);
    color: #0a0e1a;
    border: none;
    padding: 9px 20px;
    font-size: 14px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-accent:hover { background: #33dcff; transform: translateY(-1px); }

  .legal-content {
    max-width: 760px;
    margin: 0 auto;
    padding: 64px 24px 96px;
  }

  .legal-eyebrow {
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.2em;
    color: var(--accent);
    text-transform: uppercase;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .legal-eyebrow::before, .legal-eyebrow::after {
    content: '';
    width: 24px;
    height: 1px;
    background: var(--accent);
    opacity: 0.4;
  }

  .legal-title {
    font-size: 42px;
    font-weight: 800;
    color: var(--text);
    margin-bottom: 12px;
    line-height: 1.1;
  }

  .legal-date {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 48px;
  }

  .legal-intro {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.8;
    margin-bottom: 56px;
    padding: 24px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-left: 3px solid var(--accent);
    border-radius: 0 12px 12px 0;
  }

  .legal-section { margin-bottom: 40px; }

  .legal-section-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text);
    font-family: var(--font-mono);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }

  .legal-section p {
    font-size: 15px;
    color: var(--muted);
    line-height: 1.8;
    margin-bottom: 12px;
  }

  .legal-section p:last-child { margin-bottom: 0; }

  .legal-section ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .legal-section ul li {
    font-size: 15px;
    color: var(--muted);
    line-height: 1.7;
    padding-left: 20px;
    position: relative;
  }

  .legal-section ul li::before {
    content: '—';
    position: absolute;
    left: 0;
    color: var(--accent);
    font-family: var(--font-mono);
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 14px;
  }

  .data-table th {
    text-align: left;
    padding: 10px 14px;
    font-family: var(--font-mono);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--accent);
    background: var(--surface2);
    border: 1px solid var(--border);
  }

  .data-table td {
    padding: 10px 14px;
    color: var(--muted);
    border: 1px solid var(--border);
    line-height: 1.6;
    vertical-align: top;
  }

  .data-table tr:hover td { background: var(--surface); }

  .legal-link { color: var(--accent); text-decoration: none; }
  .legal-link:hover { text-decoration: underline; }

  .footer {
    border-top: 1px solid var(--border);
    padding: 32px 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  .footer-logo { font-size: 16px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .footer-links { display: flex; gap: 24px; align-items: center; }

  .footer-link {
    font-size: 13px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    transition: color 0.2s;
    text-decoration: none;
  }

  .footer-link:hover { color: var(--text); }
  .footer-copy { font-size: 12px; color: var(--muted); font-family: var(--font-mono); }

  @media (max-width: 600px) {
    .nav { padding: 16px 20px; }
    .legal-content { padding: 40px 20px 80px; }
    .legal-title { font-size: 30px; }
    .footer { padding: 24px 20px; flex-direction: column; align-items: flex-start; }
    .data-table { font-size: 13px; }
  }
`;

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <>
      <style>{sharedStyles}</style>
      <div className="page">

        <nav className="nav">
          <div className="nav-logo" onClick={() => navigate("/")}>
            <span>🐧</span>
            PassPenguin
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate("/login")}>Log in</button>
            <button className="btn-accent" onClick={() => navigate("/signup")}>Sign up free</button>
          </div>
        </nav>

        <div className="legal-content">
          <p className="legal-eyebrow">Legal</p>
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-date">Last updated: {new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>

          <div className="legal-intro">
            PassPenguin is committed to protecting your privacy. This policy explains what data we
            collect, why we collect it, and how we use it. We will never sell your personal information.
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">01 · Who We Are</h2>
            <p>
              PassPenguin is an online Salesforce certification study platform. References to "we",
              "our", or "us" in this policy refer to PassPenguin. If you have any questions about
              this policy, contact us at{" "}
              <a href="mailto:hello@passpenguin.com" className="legal-link">hello@passpenguin.com</a>.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">02 · What Data We Collect</h2>
            <p>We collect only what's necessary to provide and improve our service:</p>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Why we collect it</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Email address</td>
                  <td>Account creation, login, and service communications</td>
                </tr>
                <tr>
                  <td>Name</td>
                  <td>To personalise your experience</td>
                </tr>
                <tr>
                  <td>Exam purchase history</td>
                  <td>To grant and manage access to purchased content</td>
                </tr>
              </tbody>
            </table>
            <p style={{ marginTop: "16px" }}>
              We do <strong style={{ color: "var(--text)" }}>not</strong> store payment card details.
              All payments are handled by Stripe, who process card data on our behalf under their own
              privacy policy.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">03 · How We Collect It</h2>
            <ul>
              <li>Directly from you when you sign up, sign in with Google, or make a purchase.</li>
              <li>From Stripe when a payment is completed (order reference only — no card data).</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">04 · How We Use Your Data</h2>
            <ul>
              <li>To create and manage your account.</li>
              <li>To grant access to exams you have purchased.</li>
              <li>To send important account and service emails (e.g. password reset).</li>
              <li>To respond to your support requests.</li>
              <li>To improve the platform based on usage patterns (aggregated, not individual).</li>
            </ul>
            <p style={{ marginTop: "16px" }}>
              We will not use your data for marketing purposes without your explicit consent.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">05 · Third-Party Services</h2>
            <p>We use a small number of trusted third-party services to operate PassPenguin:</p>
            <ul>
              <li><strong style={{ color: "var(--text)" }}>Supabase</strong> — database and authentication (including Google OAuth)</li>
              <li><strong style={{ color: "var(--text)" }}>Stripe</strong> — payment processing</li>
              <li><strong style={{ color: "var(--text)" }}>Vercel</strong> — website hosting</li>
              <li><strong style={{ color: "var(--text)" }}>Google</strong> — optional sign-in via Google OAuth</li>
            </ul>
            <p style={{ marginTop: "16px" }}>
              Each of these providers has their own privacy policy and data handling practices.
              We only share the minimum data necessary for them to perform their function.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">06 · Cookies & Tracking</h2>
            <p>
              PassPenguin uses session cookies to keep you logged in. We do not use advertising
              cookies or third-party tracking pixels. We do not run ads.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">07 · Your Rights</h2>
            <p>
              Depending on where you are located, you may have rights under laws such as the GDPR
              (EU/UK) or the Australian Privacy Act. These may include the right to:
            </p>
            <ul>
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your account and associated data.</li>
              <li>Object to or restrict certain processing of your data.</li>
              <li>Data portability (receiving your data in a structured format).</li>
            </ul>
            <p style={{ marginTop: "16px" }}>
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:hello@passpenguin.com" className="legal-link">hello@passpenguin.com</a>.
              We will respond within 30 days.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">08 · Data Retention</h2>
            <p>
              We retain your account data for as long as your account is active. If you request
              account deletion, we will remove your personal data within 30 days, except where
              we are required to retain it for legal or financial compliance purposes (e.g. purchase
              records, which may be kept for up to 7 years under Australian tax law).
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">09 · Data Security</h2>
            <p>
              We take reasonable steps to protect your data, including using encrypted connections
              (HTTPS), secure authentication via Supabase, and access controls that limit who can
              view your information. No system is 100% secure — if you believe your account has
              been compromised, please contact us immediately.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">10 · Children's Privacy</h2>
            <p>
              PassPenguin is not directed at children under the age of 16. We do not knowingly
              collect personal information from anyone under 16. If you believe a child has
              provided us with their data, please contact us and we will delete it promptly.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">11 · Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes by email or by displaying a notice on our site. The date at the top of this
              page reflects when it was last updated.
            </p>
          </div>

          <div className="legal-section">
            <h2 className="legal-section-title">12 · Contact</h2>
            <p>
              For any privacy-related questions or requests, please contact us at{" "}
              <a href="mailto:hello@passpenguin.com" className="legal-link">hello@passpenguin.com</a>.
            </p>
          </div>
        </div>

        <footer className="footer">
          <div className="footer-logo">🐧 PassPenguin</div>
          <div className="footer-links">
            <a className="footer-link" href="/terms-of-service">Terms of Service</a>
            <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
            <a className="footer-link" href="mailto:hello@passpenguin.com">Contact</a>
          </div>
          <p className="footer-copy">© {new Date().getFullYear()} PassPenguin</p>
        </footer>

      </div>
    </>
  );
}